import { formatDate } from '../../Modules/Shared/Utils/date.utils';

export class AgentDevice {
  os?: Os;
  bios?: Bios;
  network?: Network;
  storage?: Storage[];
  hardware?: Hardware;
  printers?: Printer[];
  security?: Security;
  settings?: Settings;
  processor?: Processor;
  applications?: Applications;
  mapped_drives?: MappedDrive[];
  local_admin_info?: LocalAdminInfo;
  connected_devices?: ConnectedDevices;
  last_log?: string;

  constructor(data?: Partial<AgentDevice>) {
    Object.assign(this, data);
  }

  get lastLog(): string | null {
    return this.last_log ? formatDate(this.last_log) : null;
  }
}

export class Hardware {
  battery?: BatteryInfo[];
  ram_sticks?: RamStick[];
  system_uuid?: string;
  total_ram_gb?: number;

  constructor(data?: Partial<Hardware>) {
    Object.assign(this, data);
  }
}

export class RamStick {
  speed_mhz?: number;
  bank_label?: string;
  capacity_gb?: number;
  manufacturer?: string;

  constructor(data?: Partial<RamStick>) {
    Object.assign(this, data);
  }
}

export class BatteryInfo {
  battery_charge_cycles?: number;
  battery_serial_number?: string;
  battery_level_percentage?: number;
  battery_health_percentage?: number;

  constructor(data?: Partial<BatteryInfo>) {
    Object.assign(this, data);
  }
}

export class Os {
  caption?: string;
  version?: string;
  language?: string;
  build_number?: string;
  os_architecture?: string;
  reboot_required?: boolean;
  last_boot_up_time?: string;

  constructor(data?: Partial<Os>) {
    Object.assign(this, data);
  }
}

export class Storage {
  type?: string;
  status?: string;
  caption?: string;
  mounted?: boolean;
  size_gb?: number;
  encrypted?: boolean;
  file_system?: string;
  storage_remaining_gb?: number;
  storage_used_percent?: number;

  constructor(data?: Partial<Storage>) {
    Object.assign(this, data);
  }
}

export class Bios {
  version?: string;
  asset_tag?: string | null;
  dell_bios?: string | null;
  manufacturer?: string | null;
  ownership_tag?: string | null;
  wake_on_lan_enabled?: boolean;
  is_setup_password_set?: boolean;

  constructor(data?: Partial<Bios>) {
    Object.assign(this, data);
  }
}

export class Network {
  adapters?: NetworkAdapter[];
  ip_addresses?: NetworkRegisteredIp[];

  constructor(data?: Partial<Network>) {
    Object.assign(this, data);
  }
}

export class NetworkAdapter {
  name?: string;
  type?: string;
  status?: string;
  mac_address?: string;
  ip_addresses?: string[];

  constructor(data?: Partial<NetworkAdapter>) {
    Object.assign(this, data);
  }
}

export class NetworkRegisteredIp {
  ip_address?: string;
  last_registration?: string;

  constructor(data?: Partial<NetworkRegisteredIp>) {
    Object.assign(this, data);
  }
}

export class Printer {
  name?: string;
  driver?: string;
  print_saver_status?: string;

  constructor(data?: Partial<Printer>) {
    Object.assign(this, data);
  }
}

export class MappedDrive {
  letter?: string;
  user_sid?: string;
  last_used?: string | null;
  user_name?: string;
  drive_type?: string;
  persistent?: boolean;
  share_name?: string;
  remote_path?: string;
  server_name?: string;
  is_available?: boolean;
  source_method?: string;
  connection_status?: string | boolean;

  constructor(data?: Partial<MappedDrive>) {
    Object.assign(this, data);
  }
}

export class Security {
  certificates?: DeviceCertificate[];
  disk_encryption?: DiskEncryption[];
  microsoft_patches?: MicrosoftPatch[];
  microsoft_defender?: MicrosoftDefender;

  constructor(data?: Partial<Security>) {
    Object.assign(this, data);
  }
}

export class DiskEncryption {
  drive?: string;
  encrypted?: boolean;
  type?: string;
  encryption_key_length?: string;
  algorithm?: string;
  status?: string;
  recovery_key_available?: boolean;

  constructor(data?: Partial<DiskEncryption>) {
    Object.assign(this, data);
  }
}

export class MicrosoftPatch {
  kb?: string;
  description?: string;
  install_date?: string;

  constructor(data?: Partial<MicrosoftPatch>) {
    Object.assign(this, data);
  }
}

export class MicrosoftDefender {
  status?: string;
  computer_id?: string;
  error_message?: string | null;
  full_scan_age?: number;
  computer_state?: number;
  quick_scan_age?: number;
  am_running_mode?: boolean;
  reboot_required?: boolean;
  am_engine_version?: string;
  antivirus_enabled?: boolean;
  nas_signature_age?: number;
  am_product_version?: string;
  am_service_enabled?: boolean;
  am_service_version?: string;
  full_scan_end_time?: string;
  is_virtual_machine?: boolean;
  antispyware_enabled?: boolean;
  quick_scan_end_time?: string;
  full_scan_start_time?: string;
  last_full_scan_source?: number;
  nas_signature_version?: string;
  quick_scan_start_time?: string;
  last_quick_scan_source?: number;
  antivirus_signature_age?: number;
  ioav_protection_enabled?: boolean;
  behavior_monitor_enabled?: boolean;
  tamper_protection_source?: number;
  antispyware_signature_age?: number;
  is_engine_update_available?: boolean;
  nas_signature_last_updated?: string;
  antivirus_signature_version?: string;
  is_platform_update_available?: boolean;
  on_access_protection_enabled?: boolean;
  real_time_protection_enabled?: boolean;
  real_time_signature_delivery?: boolean;
  antispyware_signature_version?: string;
  is_signature_update_available?: boolean;
  antivirus_signature_last_updated?: string;
  network_inspection_system_enabled?: boolean;
  antispyware_signature_last_updated?: string;

  constructor(data?: Partial<MicrosoftDefender>) {
    Object.assign(this, data);
  }
}

export class DeviceCertificate {
  issuer?: string;
  connect?: string;
  context?: string;
  subject?: string;
  version?: string;
  thumbprint?: string;
  valid_from?: string;
  valid_until?: string;
  serial_number?: string;

  constructor(data?: Partial<DeviceCertificate>) {
    Object.assign(this, data);
  }
}

export class Processor {
  cpu_name?: string;
  cpu_cores?: number;
  cpu_speed_mhz?: number;
  cpu_manufacturer?: string;
  cpu_logical_processors?: number;
  cpu_physical_processors?: number;

  constructor(data?: Partial<Processor>) {
    Object.assign(this, data);
  }
}

export class Applications {
  software_inventory?: SoftwareInventory[];

  constructor(data?: Partial<Applications>) {
    Object.assign(this, data);
  }
}

export class SoftwareInventory {
  name?: string;
  version?: string | null;
  publisher?: string | null;

  constructor(data?: Partial<SoftwareInventory>) {
    Object.assign(this, data);
  }
}

export class Settings {
  services?: Service[];
  scheduled_tasks?: ScheduledTask[];
  startup_programs?: StartupProgram[];
  shortcut_programs?: ShortcutProgram[];
  environment_variables?: EnvironmentVariable[];
  resultant_set_of_policy?: ResultantPolicy[];

  constructor(data?: Partial<Settings>) {
    Object.assign(this, data);
  }
}

export class Service {
  service_name?: string;
  service_status?: string;
  service_display_name?: string;
  service_startup_mode?: string;

  constructor(data?: Partial<Service>) {
    Object.assign(this, data);
  }
}

export class ScheduledTask {
  author?: string | null;
  status?: string;
  account?: string | null;
  command?: string | null;
  argument?: string | null;
  task_name?: string;
  account_type?: string | null;

  constructor(data?: Partial<ScheduledTask>) {
    Object.assign(this, data);
  }
}

export class StartupProgram {
  name?: string;
  command?: string;
  enabled?: boolean;
  location?: string;
  startup_type?: string;

  constructor(data?: Partial<StartupProgram>) {
    Object.assign(this, data);
  }
}

export class ShortcutProgram {
  name?: string;
  path?: string;
  location?: string;
  arguments?: string | null;
  description?: string | null;
  target_path?: string | null;
  icon_location?: string | null;
  working_directory?: string | null;

  constructor(data?: Partial<ShortcutProgram>) {
    Object.assign(this, data);
  }
}

export class EnvironmentVariable {
  name?: string;
  value?: string;
  content?: string;

  constructor(data?: Partial<EnvironmentVariable>) {
    Object.assign(this, data);
  }
}

export class ResultantPolicy {
  name?: string;
  source?: string;
  applied?: boolean;
  policy_type?: string;

  constructor(data?: Partial<ResultantPolicy>) {
    Object.assign(this, data);
  }
}

export class ConnectedDevices {
  monitors?: Monitor[];
  usb_devices?: UsbDevice[];
  bluetooth_devices?: BluetoothDevice[];

  constructor(data?: Partial<ConnectedDevices>) {
    Object.assign(this, data);
  }
}

export class Monitor {
  monitor_id?: string;
  resolution?: string;
  monitor_type?: string;

  constructor(data?: Partial<Monitor>) {
    Object.assign(this, data);
  }
}

export class UsbDevice {
  name?: string;
  class?: string;
  device_id?: string;

  constructor(data?: Partial<UsbDevice>) {
    Object.assign(this, data);
  }
}

export class BluetoothDevice {
  name?: string;
  device_id?: string;
  error_code?: string;
  manufacturer?: string | null;

  constructor(data?: Partial<BluetoothDevice>) {
    Object.assign(this, data);
  }
}

export class LocalAdminInfo {
  admin_groups?: AdminGroup[];
  admin_accounts?: AdminAccount[];

  constructor(data?: Partial<LocalAdminInfo>) {
    Object.assign(this, data);
  }
}

export class AdminGroup {
  name?: string;
  description?: string;
  sid?: string;
  members?: AdminAccount[];

  constructor(data?: Partial<AdminGroup>) {
    Object.assign(this, data);
  }
}

export class AdminAccount {
  sid?: string;
  name?: string;
  account_type?: string;
  principal_source?: string;

  constructor(data?: Partial<AdminAccount>) {
    Object.assign(this, data);
  }
}
